import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { useT } from '@/lib/i18n';
import { useAuthStore } from '@/lib/auth-store';
import { activateAccount, me } from '@/lib/auth-api';

/**
 * Aktivasi akun untuk user yang sudah punya akun Peruri (dari pendaftaran
 * sebelumnya) tapi belum ada record di sistem lokal ini.
 *
 * Backend akan:
 *   1. checkCertificate(email) → pastikan terdaftar & cert active
 *   2. Match phone user input dengan phone Peruri (2-factor lunak)
 *   3. Create users + contacts + password + registration, auto-login
 */
export default function ActivateScreen() {
    const t = useT();
    const navigate = useNavigate();
    const setAuth = useAuthStore((s) => s.setAuth);

    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [nik, setNik] = useState('');
    const [fullName, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const canSubmit =
        email.length > 4 &&
        /^8\d{8,12}$/.test(phone) &&
        nik.length === 16 &&
        fullName.length > 0 &&
        password.length >= 8 &&
        password === passwordConfirm &&
        !submitting;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        try {
            setSubmitting(true);
            setErr(null);
            await activateAccount({
                email,
                phone,
                nik,
                fullName,
                password,
                password_confirmation: passwordConfirm,
            });
            const payload = await me();
            setAuth(payload.user, payload.registration);
            navigate('/app/dashboard', { replace: true });
        } catch (e) {
            setErr(e instanceof Error ? e.message : t.somethingWrong);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MobileFrame showBack>
            <form
                onSubmit={handleSubmit}
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 24px 28px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background:
                                'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Icon name="shieldCheck" size={20} color="white" />
                    </div>
                    <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>
                        Aktivasi Akun Peruri
                    </h1>
                </div>
                <p
                    style={{
                        fontSize: 13,
                        lineHeight: 1.55,
                        color: 'var(--ink-500)',
                        marginBottom: 20,
                    }}
                >
                    Sudah punya akun Peruri? Daftarkan akun ke sistem ini sekali saja. Bisa dipakai
                    baik untuk akun yang sertifikatnya sudah aktif, maupun yang masih{' '}
                    <b>menunggu verifikasi</b> tim Peruri. Setelah aktivasi, Anda bisa login dan
                    pantau status-nya.
                </p>

                <div
                    style={{
                        display: 'flex',
                        gap: 10,
                        padding: '10px 12px',
                        marginBottom: 18,
                        background: 'var(--primary-soft)',
                        borderRadius: 10,
                    }}
                >
                    <Icon name="info" size={16} color="var(--primary)" />
                    <span style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--primary-dark)' }}>
                        Kami akan verifikasi kepemilikan akun dengan mencocokkan email & nomor HP
                        Anda di sistem Peruri.
                    </span>
                </div>

                <label className="field-label">Email (yang terdaftar di Peruri)</label>
                <input
                    className="field-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@email.com"
                    autoComplete="email"
                    autoFocus
                />

                <label className="field-label" style={{ marginTop: 14 }}>
                    Nomor HP (harus match dengan data Peruri)
                </label>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 48,
                        border: '1px solid var(--ink-200)',
                        borderRadius: 10,
                        overflow: 'hidden',
                        background: 'var(--surface)',
                    }}
                >
                    <div
                        style={{
                            padding: '0 12px',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--ink-700)',
                            background: 'var(--ink-50)',
                            borderRight: '1px solid var(--ink-200)',
                        }}
                    >
                        +62
                    </div>
                    <input
                        className="field-input"
                        style={{ border: 'none', height: '100%' }}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                        placeholder="812 3456 7890"
                        inputMode="numeric"
                    />
                </div>

                <label className="field-label" style={{ marginTop: 14 }}>
                    NIK (16 digit)
                </label>
                <input
                    className="field-input"
                    value={nik}
                    onChange={(e) => setNik(e.target.value.replace(/\D/g, '').slice(0, 16))}
                    placeholder="3201•••••••••••"
                    inputMode="numeric"
                    maxLength={16}
                />

                <label className="field-label" style={{ marginTop: 14 }}>
                    Nama Lengkap
                </label>
                <input
                    className="field-input"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value.toUpperCase())}
                    placeholder="SEPERTI DI KTP"
                />

                <label className="field-label" style={{ marginTop: 14 }}>
                    Password Baru (untuk login di sistem ini)
                </label>
                <input
                    className={`field-input${password.length > 0 && password.length < 8 ? ' error' : ''}`}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                />
                {password.length > 0 && password.length < 8 ? (
                    <p className="field-hint error">Password minimal 8 karakter ({password.length}/8)</p>
                ) : (
                    <p className="field-hint">Minimal 8 karakter</p>
                )}

                <label className="field-label" style={{ marginTop: 10 }}>
                    Konfirmasi Password
                </label>
                <input
                    className={`field-input${passwordConfirm.length > 0 && password !== passwordConfirm ? ' error' : ''}`}
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                />
                {passwordConfirm.length > 0 && password !== passwordConfirm && (
                    <p className="field-hint error">Password tidak sama dengan konfirmasi</p>
                )}
                {passwordConfirm.length > 0 && password === passwordConfirm && password.length >= 8 && (
                    <p className="field-hint" style={{ color: 'var(--success)' }}>
                        ✓ Password cocok
                    </p>
                )}

                {err && (
                    <div
                        className="chip chip-error"
                        style={{ marginTop: 14, alignSelf: 'flex-start' }}
                    >
                        <Icon name="alert" size={14} /> {err}
                    </div>
                )}

                <div style={{ flex: 1, minHeight: 20 }} />

                <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: 16 }}
                    disabled={!canSubmit}
                >
                    {submitting ? <div className="spinner" /> : 'Aktivasi & Masuk'}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ width: '100%', marginTop: 8 }}
                    onClick={() => navigate('/app/login')}
                >
                    Kembali ke Login
                </button>
            </form>
        </MobileFrame>
    );
}
